import axios from "axios"
import { supabase } from "../supabaseClient"

const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api/whatsapp` : "/api/whatsapp",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add request interceptor to include user ID and company name
api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      config.headers["user-id"] = session.user.id
    }

    // Check if admin has selected a company
    try {
      const selectedCompanyName = localStorage.getItem("selectedCompanyName")
      if (selectedCompanyName) {
        config.headers["x-company-name"] = selectedCompanyName
      }
    } catch (e) {
      // Silently fail if localStorage access fails
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export interface WhatsAppKnowledgeBaseSource {
  id: number
  company_name: string
  knowledge_base_id: string
  original_filename: string | null
  mime_type: string | null
  byte_size: number | null
  source_type: "pdf" | "docx" | "txt" | "url" | "other"
  storage_path: string | null
  uploaded_by: string | null
  created_at: string
  updated_at: string
}

export interface WhatsAppKnowledgeBase {
  knowledge_base_id: string
  knowledge_base_name: string
  company_name: string
  description?: string
  status: "in_progress" | "complete" | "error"
  chunk_count: number
  source_count: number
  created_at: string
  updated_at: string
  sources?: WhatsAppKnowledgeBaseSource[]
}

export interface KnowledgeBaseText {
  title: string
  text: string
}

export const whatsappKnowledgeBaseService = {
  list: async (companyName?: string): Promise<WhatsAppKnowledgeBase[]> => {
    const url = companyName ? `/knowledge-bases?company_name=${encodeURIComponent(companyName)}` : "/knowledge-bases"
    const response = await api.get<WhatsAppKnowledgeBase[]>(url)
    return response.data
  },

  get: async (knowledgeBaseId: string): Promise<WhatsAppKnowledgeBase> => {
    const response = await api.get<WhatsAppKnowledgeBase>(`/knowledge-bases/${knowledgeBaseId}`)
    return response.data
  },

  create: async (
    name: string,
    texts?: KnowledgeBaseText[],
    files?: File[],
    companyName?: string,
  ): Promise<WhatsAppKnowledgeBase> => {
    const formData = new FormData()
    formData.append("name", name)

    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      formData.append("userId", session.user.id)
    }

    if (companyName) {
      formData.append("company_name", companyName)
    }

    if (texts && texts.length > 0) {
      formData.append("texts", JSON.stringify(texts))
    }

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file)
      })
    }

    const response = await api.post<WhatsAppKnowledgeBase>("/knowledge-bases", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response.data
  },

  delete: async (knowledgeBaseId: string): Promise<void> => {
    await api.delete(`/knowledge-bases/${knowledgeBaseId}`)
  },

  addSources: async (
    knowledgeBaseId: string,
    texts?: KnowledgeBaseText[],
    files?: File[],
  ): Promise<WhatsAppKnowledgeBase> => {
    const formData = new FormData()

    if (texts && texts.length > 0) {
      formData.append("texts", JSON.stringify(texts))
    }

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file)
      })
    }

    const response = await api.post<WhatsAppKnowledgeBase>(`/knowledge-bases/${knowledgeBaseId}/sources`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response.data
  },

  removeSource: async (knowledgeBaseId: string, sourceId: number): Promise<void> => {
    await api.delete(`/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}`)
  },

  downloadSource: async (knowledgeBaseId: string, sourceId: number): Promise<void> => {
    const response = await api.get(`/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}/download`, {
      responseType: "blob", // Ensure we get the response as a blob
    })

    // Check if response is JSON (contains url) or direct blob (text content)
    const contentType = String(response.headers["content-type"] ?? "")

    if (contentType && contentType.includes("application/json")) {
      // For file sources, backend returns JSON with signed URL
      const reader = new FileReader()
      reader.onload = () => {
        const jsonData = JSON.parse(reader.result as string)
        if (jsonData.url) {
          window.open(jsonData.url, "_blank")
        }
      }
      reader.readAsText(response.data)
    } else {
      // For text sources, backend sends the file directly as blob
      const contentDisposition = response.headers["content-disposition"]
      let filename = "download.txt"

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1]
        }
      }

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  },
}
